import { api, type KeyDirectoryEntry } from '../../api/client';
import { sedrApi, type SedrGuestInvite, type SedrMember, type SedrReportRecipient } from '../../api/sedr';
import { getAppState } from '../../app/index';
import { createSedrAccountGuestInvite } from '../../crypto/sedrAccountGuestInvite';
import { wrapSedrRoomKeyForMember } from '../../crypto/sedrRoomKey';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { showConfirmDialog } from '../../ui/confirmDialog';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { showLinkCopyDialog } from '../../ui/linkCopyDialog';
import { showSedrGuestInviteEmailDialog } from '../../ui/sedrGuestInviteEmail';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';
import { countReportRecipients } from './sedrReportRecipients';

export type SedrAccessRowKind = 'user' | 'guest';

export type SedrAccessGuestStatus = 'active' | 'expired' | 'revoked';

export interface SedrAccessRow {
  id: string;
  kind: SedrAccessRowKind;
  name: string;
  organization: string | null;
  email: string | null;
  role: string;
  canApprove: boolean;
  allowCommenting: boolean;
  /** True when the principal can currently use access (not revoked/expired). */
  active: boolean;
  guestStatus: SedrAccessGuestStatus | null;
  expiresAt: string | null;
  totpEnrolled: boolean | null;
  sortName: string;
  accessMode: string | null;
  claimedAt: string | null;
}

export function guestInviteAccessStatus(
  guest: Pick<SedrGuestInvite, 'revokedAt' | 'expiresAt'>,
  nowMs: number = Date.now(),
): SedrAccessGuestStatus {
  if (guest.revokedAt) {
    return 'revoked';
  }
  if (guest.expiresAt) {
    const expiresMs = Date.parse(guest.expiresAt);
    if (Number.isFinite(expiresMs) && expiresMs <= nowMs) {
      return 'expired';
    }
  }
  return 'active';
}

export function buildAccessRows(
  members: SedrMember[],
  guests: SedrGuestInvite[],
  nowMs: number = Date.now(),
): SedrAccessRow[] {
  const rows: SedrAccessRow[] = [
    ...members.map((member) => ({
      id: member.userId,
      kind: 'user' as const,
      name: member.displayName,
      organization: null,
      email: null,
      role: member.permission,
      canApprove: Boolean(member.canApprove),
      allowCommenting: false,
      active: true,
      guestStatus: null,
      expiresAt: null,
      totpEnrolled: null,
      sortName: member.displayName.toLocaleLowerCase(),
      accessMode: null,
      claimedAt: null,
    })),
    ...guests.map((guest) => {
      const guestStatus = guestInviteAccessStatus(guest, nowMs);
      return {
        id: guest.id,
        kind: 'guest' as const,
        name: guest.displayName,
        organization: guest.organization,
        email: guest.email,
        role: guest.guestRole ?? 'GuestReader',
        canApprove: (guest.guestRole ?? 'GuestReader') === 'GuestApprover',
        allowCommenting: Boolean(guest.allowCommenting),
        active: guestStatus === 'active',
        guestStatus,
        expiresAt: guest.expiresAt ?? null,
        totpEnrolled: Boolean(guest.totpEnrolled),
        sortName: guest.displayName.toLocaleLowerCase(),
        accessMode: guest.accessMode ?? null,
        claimedAt: guest.claimedAt ?? null,
      };
    }),
  ];

  rows.sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }
    return a.sortName.localeCompare(b.sortName);
  });
  return rows;
}

function tSedrPermission(permission: string): string {
  if (permission === 'Guest' || permission === 'GuestReader' || permission === 'GuestApprover') {
    const key = `sedr.access.permission.${permission === 'Guest' ? 'Guest' : permission}`;
    const translated = t(key);
    return translated === key ? permission : translated;
  }
  const key = `sedr.access.permission.${permission}`;
  const translated = t(key);
  return translated === key ? permission : translated;
}

function tAccessType(kind: SedrAccessRowKind): string {
  return kind === 'user' ? t('sedr.access.typeUser') : t('sedr.access.typeGuest');
}

export interface SedrAccessPanelOptions {
  roomId: string;
  roomTitle: string;
  canManage: boolean;
  requireGuestTotp: boolean;
  roomType: string;
  allowGuestLinks: boolean;
  getRoomKey: () => Promise<Uint8Array>;
  onChanged?: () => void | Promise<void>;
  onOpenReports?: () => void;
}

export async function renderSedrAccessPanel(
  host: HTMLElement,
  options: SedrAccessPanelOptions,
): Promise<void> {
  host.innerHTML = `<p class="muted" data-i18n="status.loading"></p>`;
  applyI18n(host);

  const state = getAppState();
  if (state.kind !== 'authenticated') {
    host.replaceChildren();
    return;
  }
  const currentUserId = state.session.userId;
  const { roomId, canManage } = options;
  const requireGuestTotp = options.requireGuestTotp;
  const allowGuestLinks = options.allowGuestLinks;
  const roomType = options.roomType || 'ExternalAudit';
  const isInternalAudit = roomType === 'InternalAudit';

  let members: SedrMember[] = [];
  let guests: SedrGuestInvite[] = [];
  let reportRecipients: SedrReportRecipient[] = [];

  try {
    if (canManage) {
      [members, guests, reportRecipients] = await Promise.all([
        sedrApi.listMembers(roomId),
        sedrApi.listGuestInvites(roomId),
        sedrApi.listReportRecipients(roomId),
      ]);
    } else {
      [members, guests] = await Promise.all([
        sedrApi.listMembers(roomId),
        sedrApi.listGuestInvites(roomId),
      ]);
    }
  } catch (cause) {
    console.error('Load room access failed', cause);
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return;
  }

  const panel = document.createElement('div');
  panel.className = 'sedr-tab-panel sedr-access-panel files-access-panel';
  panel.innerHTML = `
    ${canManage ? `
      <div class="files-access-toolbar" id="sedr-access-toolbar">
        <button type="button" class="button primary" id="sedr-invite-user" data-i18n="sedr.access.inviteUser"></button>
        <button type="button" class="button ghost" id="sedr-invite-guest" data-i18n="sedr.access.inviteGuest" ${allowGuestLinks ? '' : 'hidden'}></button>
      </div>
      <p class="muted" data-i18n="sedr.roomType.${roomType}"></p>
    ` : ''}
    <p class="error-text" id="sedr-access-error" hidden></p>
    <p class="muted" id="sedr-access-info" hidden></p>
    <div class="files-table-wrap">
      <table class="data-table sedr-access-table">
        <thead>
          <tr>
            <th data-i18n="sedr.access.memberName"></th>
            <th data-i18n="sedr.access.type"></th>
            <th data-i18n="sedr.guestInvite.organization"></th>
            <th data-i18n="sedr.guestInvite.email"></th>
            <th data-i18n="sedr.access.permission"></th>
            ${isInternalAudit ? `<th data-i18n="sedr.access.canApprove"></th>` : ''}
            <th data-i18n="sedr.access.guestStatus"></th>
            <th data-i18n="sedr.access.totpStatus"></th>
            ${canManage ? `<th class="data-table__actions-heading" data-i18n="sedr.access.actions"></th>` : ''}
          </tr>
        </thead>
        <tbody id="sedr-access-body"></tbody>
      </table>
    </div>
  `;

  host.replaceChildren(panel);
  applyI18n(panel);

  if (canManage) {
    const counts = countReportRecipients(reportRecipients);
    const summary = document.createElement('div');
    summary.className = 'sedr-access-report-summary';
    const summaryText = document.createElement('p');
    summaryText.className = 'muted';
    if (counts.total === 0) {
      summaryText.textContent = t('sedr.access.reportSummaryEmpty');
    } else {
      summaryText.textContent = t('sedr.access.reportSummary', {
        members: String(counts.members),
        guests: String(counts.guests),
        externals: String(counts.externals),
        optedOut: String(counts.optedOut),
      });
    }
    const manageBtn = document.createElement('button');
    manageBtn.type = 'button';
    manageBtn.className = 'button ghost';
    manageBtn.setAttribute('data-i18n', 'sedr.access.reportManage');
    manageBtn.addEventListener('click', () => options.onOpenReports?.());
    summary.append(summaryText, manageBtn);
    panel.append(summary);
    applyI18n(summary);
  }

  const body = panel.querySelector('#sedr-access-body') as HTMLTableSectionElement;
  const errorEl = panel.querySelector('#sedr-access-error') as HTMLParagraphElement;
  const infoEl = panel.querySelector('#sedr-access-info') as HTMLParagraphElement;
  let busy = false;

  const showError = (message: string) => {
    errorEl.hidden = false;
    errorEl.textContent = message;
    infoEl.hidden = true;
  };

  const showInfo = (message: string) => {
    infoEl.hidden = false;
    infoEl.textContent = message;
    errorEl.hidden = true;
  };

  const clearMessages = () => {
    errorEl.hidden = true;
    infoEl.hidden = true;
    errorEl.textContent = '';
    infoEl.textContent = '';
  };

  const adminCount = () => members.filter((m) => m.permission === 'Admin').length;

  const renderRows = () => {
    body.replaceChildren();
    const rows = buildAccessRows(members, guests);
    const colCount = (canManage ? 8 : 7) + (isInternalAudit ? 1 : 0);
    if (rows.length === 0) {
      const empty = document.createElement('tr');
      empty.innerHTML = `<td colspan="${colCount}" class="muted" data-i18n="sedr.access.empty"></td>`;
      body.append(empty);
      applyI18n(empty);
      return;
    }

    for (const row of rows) {
      const tr = document.createElement('tr');
      if (!row.active) {
        tr.classList.add('sedr-access-row--revoked');
      }
      tr.innerHTML = `
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td class="sedr-access-permission-cell"></td>
        ${isInternalAudit ? `<td class="sedr-access-can-approve-cell"></td>` : ''}
        <td></td>
        <td></td>
      `;
      const cells = tr.querySelectorAll('td');
      cells[0].textContent = row.name;
      cells[1].textContent = tAccessType(row.kind);
      cells[2].textContent = row.organization ?? '—';
      cells[3].textContent = row.email ?? '—';
      const statusIdx = isInternalAudit ? 6 : 5;
      const totpIdx = isInternalAudit ? 7 : 6;
      if (row.kind === 'guest') {
        const statusKey =
          row.guestStatus === 'revoked'
            ? 'sedr.access.guestRevoked'
            : row.guestStatus === 'expired'
              ? 'sedr.access.guestExpired'
              : 'sedr.access.guestActive';
        cells[statusIdx].textContent = t(statusKey);
        if (requireGuestTotp) {
          cells[totpIdx].textContent = row.totpEnrolled
            ? t('sedr.access.totpEnrolled')
            : t('sedr.access.totpPending');
        } else {
          cells[totpIdx].textContent = t('sedr.access.totpNotRequired');
        }
      } else {
        cells[statusIdx].textContent = '—';
        cells[totpIdx].textContent = '—';
      }

      const permissionCell = tr.querySelector('.sedr-access-permission-cell') as HTMLTableCellElement;
      if (canManage && row.kind === 'user' && row.active) {
        const select = document.createElement('select');
        select.className = 'sedr-access-permission-select';
        for (const perm of ['Reader', 'Contributor', 'Admin']) {
          const opt = document.createElement('option');
          opt.value = perm;
          opt.textContent = tSedrPermission(perm);
          if (perm === row.role) opt.selected = true;
          select.append(opt);
        }
        select.addEventListener('change', () => {
          void (async () => {
            if (busy) return;
            const next = select.value;
            if (next === row.role) return;
            busy = true;
            clearMessages();
            try {
              await sedrApi.patchMember(roomId, row.id, { permission: next });
              const member = members.find((m) => m.userId === row.id);
              if (member) member.permission = next;
              renderRows();
              await options.onChanged?.();
            } catch (cause) {
              select.value = row.role;
              showError(t(sedrErrorMessageKey(cause)));
            } finally {
              busy = false;
            }
          })();
        });
        permissionCell.append(select);
      } else {
        permissionCell.textContent = tSedrPermission(row.role);
        if (row.kind === 'guest' && row.allowCommenting) {
          permissionCell.textContent += ` · ${t('sedr.access.canComment')}`;
        }
      }

      if (isInternalAudit) {
        const canApproveCell = tr.querySelector('.sedr-access-can-approve-cell') as HTMLTableCellElement;
        if (canManage && row.kind === 'user' && row.active) {
          const checkbox = document.createElement('input');
          checkbox.type = 'checkbox';
          checkbox.checked = row.canApprove;
          checkbox.addEventListener('change', () => {
            void (async () => {
              if (busy) return;
              busy = true;
              clearMessages();
              try {
                await sedrApi.patchMember(roomId, row.id, { canApprove: checkbox.checked });
                const member = members.find((m) => m.userId === row.id);
                if (member) member.canApprove = checkbox.checked;
                await options.onChanged?.();
              } catch (cause) {
                checkbox.checked = row.canApprove;
                showError(t(sedrErrorMessageKey(cause)));
              } finally {
                busy = false;
              }
            })();
          });
          canApproveCell.append(checkbox);
        } else {
          canApproveCell.textContent = row.kind === 'user'
            ? (row.canApprove ? t('sedr.access.yes') : t('sedr.access.no'))
            : '—';
        }
      }

      if (canManage) {
        const actionsCell = document.createElement('td');
        actionsCell.className = 'data-table__actions';

        if (row.kind === 'user') {
          const canRevoke =
            row.id !== currentUserId
            && (row.role !== 'Admin' || adminCount() > 2);
          if (canRevoke) {
            const revokeBtn = document.createElement('button');
            revokeBtn.type = 'button';
            revokeBtn.className = 'button subtle';
            revokeBtn.textContent = t('sedr.access.revoke');
            revokeBtn.addEventListener('click', () => {
              void (async () => {
                if (busy) return;
                const confirmed = await showConfirmDialog({
                  titleKey: 'sedr.access.revokeMemberTitle',
                  message: t('sedr.access.revokeMemberConfirm', { name: row.name }),
                  confirmLabelKey: 'sedr.access.revoke',
                });
                if (!confirmed) return;
                busy = true;
                clearMessages();
                try {
                  await sedrApi.removeMember(roomId, row.id);
                  members = members.filter((m) => m.userId !== row.id);
                  renderRows();
                  await options.onChanged?.();
                } catch (cause) {
                  showError(t(sedrErrorMessageKey(cause)));
                } finally {
                  busy = false;
                }
              })();
            });
            actionsCell.append(revokeBtn);
          } else if (row.role === 'Admin' && adminCount() <= 2 && row.id !== currentUserId) {
            const note = document.createElement('span');
            note.className = 'muted';
            note.textContent = t('sedr.access.adminQuorum');
            actionsCell.append(note);
          }
        } else if (row.guestStatus !== 'revoked') {
          if (
            !row.email
            && !row.claimedAt
            && (row.accessMode === 'LegacyFragment' || row.accessMode == null)
            && row.active
          ) {
            const setEmailBtn = document.createElement('button');
            setEmailBtn.type = 'button';
            setEmailBtn.className = 'button subtle';
            setEmailBtn.textContent = t('sedr.access.setEmail');
            setEmailBtn.addEventListener('click', () => {
              showSetGuestEmailDialog({
                roomId,
                guestId: row.id,
                guestName: row.name,
                onUpdated: async (email) => {
                  const guest = guests.find((g) => g.id === row.id);
                  if (guest) {
                    guest.email = email;
                  }
                  renderRows();
                  showInfo(t('sedr.access.setEmailSuccess', { name: row.name }));
                  await options.onChanged?.();
                },
                onError: showError,
              });
            });
            actionsCell.append(setEmailBtn);
          }

          if (requireGuestTotp && row.active) {
            const resetBtn = document.createElement('button');
            resetBtn.type = 'button';
            resetBtn.className = 'button subtle';
            resetBtn.textContent = t('sedr.access.resetTotp');
            resetBtn.addEventListener('click', () => {
              void (async () => {
                if (busy) return;
                const confirmed = await showConfirmDialog({
                  titleKey: 'sedr.access.resetTotpTitle',
                  message: t('sedr.access.resetTotpConfirm', { name: row.name }),
                  confirmLabelKey: 'sedr.access.resetTotp',
                });
                if (!confirmed) return;
                busy = true;
                clearMessages();
                try {
                  await sedrApi.resetGuestTotp(roomId, row.id);
                  const guest = guests.find((g) => g.id === row.id);
                  if (guest) {
                    guest.totpEnrolled = false;
                  }
                  renderRows();
                  showInfo(t('sedr.access.resetTotpSuccess', { name: row.name }));
                  await options.onChanged?.();
                } catch (cause) {
                  showError(t(sedrErrorMessageKey(cause)));
                } finally {
                  busy = false;
                }
              })();
            });
            actionsCell.append(resetBtn);
          }

          const changeAccessBtn = document.createElement('button');
          changeAccessBtn.type = 'button';
          changeAccessBtn.className = 'button subtle';
          changeAccessBtn.textContent = t('sedr.access.changeAccess');
          changeAccessBtn.addEventListener('click', () => {
            showChangeGuestAccessDialog({
              roomId,
              guestId: row.id,
              guestName: row.name,
              guestRole: row.role,
              allowCommenting: row.allowCommenting,
              expiresAt: row.expiresAt,
              onUpdated: async (updated) => {
                const guest = guests.find((g) => g.id === row.id);
                if (guest) {
                  guest.expiresAt = updated.expiresAt;
                  guest.guestRole = updated.guestRole;
                  guest.allowCommenting = updated.allowCommenting;
                }
                renderRows();
                showInfo(t('sedr.access.changeAccessSuccess', { name: row.name }));
                await options.onChanged?.();
              },
              onError: showError,
            });
          });
          actionsCell.append(changeAccessBtn);

          const revokeBtn = document.createElement('button');
          revokeBtn.type = 'button';
          revokeBtn.className = 'button subtle';
          revokeBtn.textContent = t('sedr.access.revoke');
          revokeBtn.addEventListener('click', () => {
            void (async () => {
              if (busy) return;
              const confirmed = await showConfirmDialog({
                titleKey: 'sedr.access.revokeGuestTitle',
                message: t('sedr.access.revokeGuestConfirm', { name: row.name }),
                confirmLabelKey: 'sedr.access.revoke',
              });
              if (!confirmed) return;
              busy = true;
              clearMessages();
              try {
                await sedrApi.revokeGuestInvite(roomId, row.id);
                const guest = guests.find((g) => g.id === row.id);
                if (guest) {
                  guest.revokedAt = new Date().toISOString();
                }
                renderRows();
                await options.onChanged?.();
              } catch (cause) {
                showError(t(sedrErrorMessageKey(cause)));
              } finally {
                busy = false;
              }
            })();
          });
          actionsCell.append(revokeBtn);
        }

        tr.append(actionsCell);
      }

      body.append(tr);
    }

    const accessTable = panel.querySelector('table.data-table') as HTMLTableElement | null;
    if (accessTable) {
      enhanceDataTableCards(accessTable);
    }
  };

  try {
    renderRows();
  } catch (cause) {
    console.error('Render room access failed', cause);
    host.innerHTML = `<p class="error-text" data-i18n="status.error"></p>`;
    applyI18n(host);
    return;
  }

  if (canManage) {
    panel.querySelector('#sedr-invite-user')?.addEventListener('click', () => {
      void showInviteUserDialog({
        roomId,
        members,
        showCanApprove: isInternalAudit,
        getRoomKey: options.getRoomKey,
        onInvited: async (member) => {
          members = [...members, member];
          renderRows();
          showInfo(t('sedr.access.inviteUserSuccess', { name: member.displayName }));
          await options.onChanged?.();
        },
        onError: showError,
      });
    });

    panel.querySelector('#sedr-invite-guest')?.addEventListener('click', () => {
      void showInviteGuestDialog({
        roomId,
        roomTitle: options.roomTitle,
        requireGuestTotp,
        getRoomKey: options.getRoomKey,
        onInvited: async (guest) => {
          guests = [guest, ...guests];
          renderRows();
          showInfo(t('sedr.guestInvite.created', { name: guest.displayName }));
          await options.onChanged?.();
        },
        onError: showError,
      });
    });
  }
}

interface InviteUserDialogOptions {
  roomId: string;
  members: SedrMember[];
  showCanApprove: boolean;
  getRoomKey: () => Promise<Uint8Array>;
  onInvited: (member: SedrMember) => void | Promise<void>;
  onError: (message: string) => void;
}

async function showInviteUserDialog(options: InviteUserDialogOptions): Promise<void> {
  const state = getAppState();
  if (state.kind !== 'authenticated') return;
  const currentUserId = state.session.userId;

  let directory: KeyDirectoryEntry[] = [];
  try {
    directory = await api.getKeyDirectory();
  } catch {
    options.onError(t('status.error'));
    return;
  }

  const memberUserIds = new Set(options.members.map((m) => m.userId));
  const candidates = directory.filter(
    (entry) => entry.userId !== currentUserId && !memberUserIds.has(entry.userId),
  );

  const shell = createModalShell({
    titleKey: 'sedr.access.inviteUserTitle',
    helpTopic: dialogHelpTopic('sedrInviteUser'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-invite-user-form">
      <label>
        <span data-i18n="sedr.access.inviteUser"></span>
        <select name="userId" required></select>
      </label>
      <label>
        <span data-i18n="sedr.access.permission"></span>
        <select name="permission" required>
          <option value="Reader" data-i18n="sedr.access.permission.Reader"></option>
          <option value="Contributor" data-i18n="sedr.access.permission.Contributor"></option>
          <option value="Admin" data-i18n="sedr.access.permission.Admin"></option>
        </select>
      </label>
      ${options.showCanApprove ? `
      <label class="checkbox-row">
        <input type="checkbox" name="canApprove" />
        <span data-i18n="sedr.access.canApprove"></span>
      </label>` : ''}
      <p class="error-text" id="sedr-invite-user-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'sedr-invite-user-cancel';
  cancelButton.setAttribute('data-i18n', 'info.owner.dialogClose');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-invite-user-form');
  submitButton.setAttribute('data-i18n', 'sedr.access.invite');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-invite-user-form') as HTMLFormElement;
  const userSelect = form.querySelector('select[name="userId"]') as HTMLSelectElement;
  const errorEl = shell.body.querySelector('#sedr-invite-user-error') as HTMLParagraphElement;
  const submitBtn = submitButton;

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.setAttribute('data-i18n', 'sedr.access.inviteUserPlaceholder');
  userSelect.append(placeholder);
  for (const candidate of candidates) {
    const option = document.createElement('option');
    option.value = candidate.userId;
    option.textContent = candidate.displayName;
    userSelect.append(option);
  }
  applyI18n(userSelect);
  const disabled = candidates.length === 0;
  userSelect.disabled = disabled;
  submitBtn.disabled = disabled;

  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const userId = String(data.get('userId') ?? '').trim();
      const permission = String(data.get('permission') ?? 'Reader');
      const canApprove = options.showCanApprove && data.get('canApprove') === 'on';
      if (!userId) return;
      submitBtn.disabled = true;
      errorEl.hidden = true;
      try {
        const roomKey = await options.getRoomKey();
        await sedrApi.inviteMember(options.roomId, {
          userId,
          permission,
          wrappedRoomKey: await wrapSedrRoomKeyForMember(options.roomId, roomKey, userId),
          canApprove,
        });
        const invited = directory.find((d) => d.userId === userId);
        await options.onInvited({
          userId,
          displayName: invited?.displayName ?? userId,
          permission,
          createdAt: new Date().toISOString(),
          canApprove,
        });
        close();
      } catch (cause) {
        errorEl.hidden = false;
        errorEl.textContent = t(sedrErrorMessageKey(cause));
        submitBtn.disabled = disabled;
      }
    })();
  });
}

interface InviteGuestDialogOptions {
  roomId: string;
  roomTitle: string;
  requireGuestTotp: boolean;
  getRoomKey: () => Promise<Uint8Array>;
  onInvited: (guest: SedrGuestInvite) => void | Promise<void>;
  onError: (message: string) => void;
}

async function showInviteGuestDialog(options: InviteGuestDialogOptions): Promise<void> {
  const shell = createModalShell({
    titleKey: 'sedr.access.inviteGuestTitle',
    helpTopic: dialogHelpTopic('sedrInviteGuest'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-invite-guest-form" method="post" action="#">
      ${options.requireGuestTotp
        ? `<p class="muted" data-i18n="sedr.guestInvite.totpRequiredHint"></p>`
        : ''}
      <label>
        <span data-i18n="sedr.guestInvite.displayName"></span>
        <input type="text" name="displayName" required maxlength="200" autocomplete="name" />
        <span class="muted" data-i18n="sedr.guestInvite.displayNameHint"></span>
      </label>
      <label>
        <span data-i18n="sedr.guestInvite.organization"></span>
        <input type="text" name="organization" required maxlength="200" autocomplete="organization" />
        <span class="muted" data-i18n="sedr.guestInvite.organizationHint"></span>
      </label>
      <label>
        <span data-i18n="sedr.guestInvite.email"></span>
        <input type="email" name="email" maxlength="200" autocomplete="email" required />
        <span class="muted" data-i18n="sedr.guestInvite.emailRequiredHint"></span>
      </label>
      <label>
        <span data-i18n="sedr.access.permission"></span>
        <select name="guestRole" required>
          <option value="GuestReader" data-i18n="sedr.access.permission.GuestReader"></option>
          <option value="GuestApprover" data-i18n="sedr.access.permission.GuestApprover"></option>
        </select>
      </label>
      <label class="checkbox-row">
        <input type="checkbox" name="allowCommenting" />
        <span data-i18n="sedr.guestInvite.allowCommenting"></span>
      </label>
      <label>
        <span data-i18n="sedr.guestInvite.expiresAt"></span>
        <input type="datetime-local" name="expiresAt" />
        <span class="muted" data-i18n="sedr.guestInvite.expiresAtHint"></span>
      </label>
      <p class="error-text" id="sedr-invite-guest-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'sedr-invite-guest-cancel';
  cancelButton.setAttribute('data-i18n', 'info.owner.dialogClose');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-invite-guest-form');
  submitButton.setAttribute('data-i18n', 'sedr.guestInvite.button');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-invite-guest-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-invite-guest-error') as HTMLParagraphElement;
  const submitBtn = submitButton;

  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const displayName = String(data.get('displayName') ?? '').trim();
      const organization = String(data.get('organization') ?? '').trim();
      const email = String(data.get('email') ?? '').trim();
      const expiresLocal = String(data.get('expiresAt') ?? '').trim();
      const guestRole = String(data.get('guestRole') ?? 'GuestReader');
      const allowCommenting = data.get('allowCommenting') === 'on';
      if (!displayName || !organization || !email) return;
      submitBtn.disabled = true;
      errorEl.hidden = true;
      try {
        const roomKey = await options.getRoomKey();
        const result = await createSedrAccountGuestInvite({
          roomId: options.roomId,
          roomKey,
          displayName,
          organization,
          email,
          expiresAt: expiresLocal ? new Date(expiresLocal).toISOString() : null,
          guestRole,
          allowCommenting,
        });
        if (result.outcome === 'staffRejected') {
          errorEl.hidden = false;
          errorEl.textContent = t(
            result.reason === 'staffDomain'
              ? 'sedr.guestInvite.staffDomain'
              : 'sedr.guestInvite.staffUser',
          );
          submitBtn.disabled = false;
          return;
        }
        if (result.outcome === 'guestAdded') {
          close();
          await options.onInvited({
            id: result.userId,
            displayName: result.displayName,
            organization,
            email,
            revokedAt: null,
            createdAt: new Date().toISOString(),
            expiresAt: expiresLocal ? new Date(expiresLocal).toISOString() : null,
            totpEnrolled: false,
            guestRole,
            allowCommenting,
          });
          const { showMessageDialog } = await import('../../ui/confirmDialog');
          await showMessageDialog({
            titleKey: 'sedr.guestInvite.button',
            message: t('sedr.guestInvite.alreadyKnown', { name: result.displayName }),
          });
          return;
        }
        await options.onInvited({
          id: result.guestInviteId,
          displayName,
          organization,
          email,
          revokedAt: null,
          createdAt: new Date().toISOString(),
          expiresAt: expiresLocal ? new Date(expiresLocal).toISOString() : null,
          totpEnrolled: false,
          guestRole,
          allowCommenting,
        });
        close();
        await showLinkCopyDialog(
          async () => result.guestUrl,
          {
            titleKey: 'info.owner.dialogTitle',
            warningKey: 'sedr.guestInvite.linkWarning',
            createdKey: 'info.owner.created',
            creatingTitleKey: 'files.owner.guestLinkCreating',
            creatingDetailKey: 'files.owner.guestLinkCreatingDetail',
          },
          {
            secondaryAction: {
              labelKey: 'sedr.guestInvite.emailTemplate.open',
              onClick: (guestUrl: string, helpers: { markDelivered: () => void }) => {
                showSedrGuestInviteEmailDialog({
                  guestName: displayName,
                  guestEmail: email,
                  roomTitle: options.roomTitle,
                  inviteUrl: guestUrl,
                  loginUrl: `${window.location.origin}/guest/`,
                  onDelivered: helpers.markDelivered,
                });
              },
            },
          },
        );
      } catch (cause) {
        errorEl.hidden = false;
        errorEl.textContent = t(sedrErrorMessageKey(cause));
        submitBtn.disabled = false;
      }
    })();
  });
}

function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface SetGuestEmailDialogOptions {
  roomId: string;
  guestId: string;
  guestName: string;
  onUpdated: (email: string) => void | Promise<void>;
  onError: (message: string) => void;
}

function showSetGuestEmailDialog(options: SetGuestEmailDialogOptions): void {
  const shell = createModalShell({
    titleKey: 'sedr.access.setEmailTitle',
    helpTopic: dialogHelpTopic('sedrInviteGuest'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-set-guest-email-form" method="post" action="#">
      <p class="muted">${t('sedr.access.setEmailHint', { name: options.guestName })}</p>
      <label>
        <span data-i18n="sedr.guestInvite.email"></span>
        <input type="email" name="email" maxlength="200" autocomplete="email" required />
        <span class="muted" data-i18n="sedr.access.setEmailUpgradeHint"></span>
      </label>
      <p class="error-text" id="sedr-set-guest-email-error" hidden></p>
    </form>
  `;

  shell.actions.innerHTML = `
    <button type="button" class="button ghost" id="sedr-set-guest-email-cancel" data-i18n="common.cancel"></button>
    <button type="submit" form="sedr-set-guest-email-form" class="button primary" data-i18n="sedr.access.setEmail"></button>
  `;

  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-set-guest-email-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-set-guest-email-error') as HTMLElement;
  const submitBtn = shell.actions.querySelector('button[type="submit"]') as HTMLButtonElement;
  const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement;

  const close = () => {
    unbind();
    shell.backdrop.remove();
  };

  const unbind = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  shell.actions.querySelector('#sedr-set-guest-email-cancel')?.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      errorEl.hidden = true;
      const email = emailInput.value.trim();
      if (!email) return;
      submitBtn.disabled = true;
      try {
        const lookup = await sedrApi.lookupGuestEmail(options.roomId, email);
        if (lookup.outcome === 'staffDomain') {
          errorEl.hidden = false;
          errorEl.textContent = t('sedr.guestInvite.staffDomain');
          submitBtn.disabled = false;
          return;
        }
        if (lookup.outcome === 'staffUser') {
          errorEl.hidden = false;
          errorEl.textContent = t('sedr.guestInvite.staffUser');
          submitBtn.disabled = false;
          return;
        }
        await sedrApi.patchGuestInvite(options.roomId, options.guestId, { email });
        close();
        await options.onUpdated(email);
      } catch (cause) {
        errorEl.hidden = false;
        errorEl.textContent = t(sedrErrorMessageKey(cause));
        options.onError(t(sedrErrorMessageKey(cause)));
        submitBtn.disabled = false;
      }
    })();
  });
}

interface ChangeGuestAccessDialogOptions {
  roomId: string;
  guestId: string;
  guestName: string;
  guestRole: string;
  allowCommenting: boolean;
  expiresAt: string | null;
  onUpdated: (updated: {
    expiresAt: string | null;
    guestRole: string;
    allowCommenting: boolean;
  }) => void | Promise<void>;
  onError: (message: string) => void;
}

function showChangeGuestAccessDialog(options: ChangeGuestAccessDialogOptions): void {
  const shell = createModalShell({
    titleKey: 'sedr.access.changeAccessTitle',
    helpTopic: dialogHelpTopic('sedrInviteGuest'),
  });

  const role = options.guestRole === 'GuestApprover' ? 'GuestApprover' : 'GuestReader';

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-change-guest-access-form" method="post" action="#">
      <p class="muted">${t('sedr.access.changeAccessHint', { name: options.guestName })}</p>
      <label>
        <span data-i18n="sedr.access.permission"></span>
        <select name="guestRole" required>
          <option value="GuestReader" data-i18n="sedr.access.permission.GuestReader"></option>
          <option value="GuestApprover" data-i18n="sedr.access.permission.GuestApprover"></option>
        </select>
      </label>
      <label class="checkbox-row">
        <input type="checkbox" name="allowCommenting" />
        <span data-i18n="sedr.guestInvite.allowCommenting"></span>
      </label>
      <label>
        <span data-i18n="sedr.guestInvite.expiresAt"></span>
        <input type="datetime-local" name="expiresAt" />
        <span class="muted" data-i18n="sedr.guestInvite.expiresAtHint"></span>
      </label>
      <p class="error-text" id="sedr-change-guest-access-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-change-guest-access-form');
  submitButton.setAttribute('data-i18n', 'sedr.drawer.save');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-change-guest-access-form') as HTMLFormElement;
  const roleSelect = form.querySelector('select[name="guestRole"]') as HTMLSelectElement;
  const commentingInput = form.querySelector('input[name="allowCommenting"]') as HTMLInputElement;
  const expiresInput = form.querySelector('input[name="expiresAt"]') as HTMLInputElement;
  const errorEl = shell.body.querySelector('#sedr-change-guest-access-error') as HTMLParagraphElement;
  roleSelect.value = role;
  commentingInput.checked = options.allowCommenting;
  expiresInput.value = toDateTimeLocalValue(options.expiresAt);

  const unbindLocale = bindLocaleRefresh(() => applyI18n(shell.dialog));
  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const guestRole = String(data.get('guestRole') ?? 'GuestReader');
      const allowCommenting = data.get('allowCommenting') === 'on';
      const expiresLocal = String(data.get('expiresAt') ?? '').trim();
      submitButton.disabled = true;
      errorEl.hidden = true;
      try {
        const updated = await sedrApi.patchGuestInvite(options.roomId, options.guestId, {
          guestRole,
          allowCommenting,
          ...(expiresLocal
            ? { expiresAt: new Date(expiresLocal).toISOString() }
            : { clearExpiresAt: true }),
        });
        await options.onUpdated({
          expiresAt: updated.expiresAt ?? null,
          guestRole: updated.guestRole ?? guestRole,
          allowCommenting: Boolean(updated.allowCommenting),
        });
        close();
      } catch (cause) {
        errorEl.hidden = false;
        errorEl.textContent = t(sedrErrorMessageKey(cause));
        submitButton.disabled = false;
        options.onError(t(sedrErrorMessageKey(cause)));
      }
    })();
  });
}
